/** In-app annex route prefix — single source of truth for annex reference paths. */
export const ANNEX_PATH_PREFIX = '/annex';

/** Separator between a parent definition slug and a nested block id in annex hashes. */
export const ANNEX_UUID_SEPARATOR = '-uuid-';

const HTTP_URL_RE = /^https?:\/\//i;

/** Slug chars allowed in annex hash fragments (parent slug segment). */
const ANNEX_HASH_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/i;

export type ParsedAnnexHash = {
  parentSlug: string;
  childBlockId?: string;
};

export type ParsedAnnexPath = {
  /** Raw search string including leading `?`, or empty when absent. */
  search: string;
  hash: ParsedAnnexHash | null;
};

type SplitAnnexPath = {
  search: string;
  hashPart: string | null;
};

function splitAnnexPath(value: string): SplitAnnexPath | null {
  if (!value.startsWith(ANNEX_PATH_PREFIX)) return null;

  const rest = value.slice(ANNEX_PATH_PREFIX.length);
  if (rest.length === 0) {
    return { search: '', hashPart: null };
  }

  if (rest.startsWith('?')) {
    const hashIndex = rest.indexOf('#');
    if (hashIndex === -1) {
      const search = rest;
      if (search.length <= 1) return null;
      return { search, hashPart: null };
    }
    const search = rest.slice(0, hashIndex);
    if (search.length <= 1) return null;
    const hashPart = rest.slice(hashIndex + 1);
    if (hashPart.length === 0) return null;
    return { search, hashPart };
  }

  if (rest.startsWith('#')) {
    const hashPart = rest.slice(1);
    if (hashPart.length === 0) return null;
    return { search: '', hashPart };
  }

  return null;
}

function parseHashPart(hashPart: string): ParsedAnnexHash | null {
  const sepIndex = hashPart.lastIndexOf(ANNEX_UUID_SEPARATOR);
  if (sepIndex === -1) {
    if (!ANNEX_HASH_SLUG_RE.test(hashPart)) return null;
    return { parentSlug: hashPart };
  }

  const parentSlug = hashPart.slice(0, sepIndex);
  const childBlockId = hashPart.slice(sepIndex + ANNEX_UUID_SEPARATOR.length);
  if (
    parentSlug.length === 0 ||
    childBlockId.length === 0 ||
    !ANNEX_HASH_SLUG_RE.test(parentSlug)
  ) {
    return null;
  }
  return { parentSlug, childBlockId };
}

/** True when `value` is an in-app annex path (e.g. `/annex#world-rankings-competition`). */
export function isAnnexPath(value: string): boolean {
  return value.startsWith(ANNEX_PATH_PREFIX);
}

/** True when `value` is an http(s) URL or an allowed in-app annex path. */
export function isAllowedExternalUrl(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  if (isAnnexPath(trimmed)) return isValidAnnexPath(trimmed);
  return HTTP_URL_RE.test(trimmed);
}

/**
 * Validates annex path shape:
 * - `/annex`
 * - `/annex?language=EN`
 * - `/annex?language=EN#term-slug`
 * - `/annex#term-slug` optionally with `-uuid-{blockId}` suffix
 */
export function isValidAnnexPath(value: string): boolean {
  return parseAnnexPath(value) !== null;
}

/**
 * Parse an annex path into optional search params and hash target.
 * Returns null when the path shape is invalid.
 */
export function parseAnnexPath(externalUrl: string): ParsedAnnexPath | null {
  const split = splitAnnexPath(externalUrl);
  if (!split) return null;

  if (split.hashPart === null) {
    return { search: split.search, hash: null };
  }

  const hash = parseHashPart(split.hashPart);
  if (!hash) return null;
  return { search: split.search, hash };
}

/**
 * Build an annex reference path. When `childBlockId` is set, appends
 * `-uuid-{blockId}` so nested targets survive renumbering.
 */
export function buildAnnexPath(
  parentSlug: string,
  childBlockId?: string,
  search?: string,
): string {
  const normalizedSearch = search?.startsWith('?')
    ? search
    : search
      ? `?${search}`
      : '';
  const base = `${ANNEX_PATH_PREFIX}${normalizedSearch}#${parentSlug}`;
  if (!childBlockId) return base;
  return `${base}${ANNEX_UUID_SEPARATOR}${childBlockId}`;
}

/**
 * Strip a legacy `occ=<n>` discriminator from a URL, preserving every other
 * search param and the hash. `occ` was a workaround for the DB uniqueness on
 * `(sourceId, externalUrl, nodeId, kind)` that collapsed repeated mentions of
 * the same URL/annex path in one block into a single row (see migration
 * 20260804120000_reference_allow_duplicate_url, which removed that
 * constraint — every occurrence is now its own row, distinguished by its
 * row-scoped token, so the discriminator is no longer needed anywhere).
 *
 * Handles both annex paths (`/annex?occ=2#slug`) and plain http(s) URLs
 * (`https://example.com/x?occ=2`). Returns the input unchanged when it has
 * no `occ` param.
 */
export function stripUrlOccurrence(url: string): string {
  const hashIndex = url.indexOf('#');
  const beforeHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex);

  const queryIndex = beforeHash.indexOf('?');
  if (queryIndex === -1) return url;

  const base = beforeHash.slice(0, queryIndex);
  const search = beforeHash.slice(queryIndex + 1);
  if (!/(?:^|&)occ=/.test(search)) return url;

  const params = new URLSearchParams(search);
  params.delete('occ');
  const nextSearch = params.toString();
  return nextSearch.length > 0
    ? `${base}?${nextSearch}${hash}`
    : `${base}${hash}`;
}

/**
 * Parse the hash portion of an annex path into parent slug and optional child block id.
 * Splits on the last `-uuid-` occurrence so slugs may contain dashes.
 */
export function parseAnnexHash(externalUrl: string): ParsedAnnexHash | null {
  const parsed = parseAnnexPath(externalUrl);
  if (!parsed) return null;
  if (!parsed.hash) return { parentSlug: '' };
  return parsed.hash;
}

/** Annex hash fragment (without `#`) for router navigation from a stored externalUrl. */
export function annexHashFromExternalUrl(externalUrl: string): string | null {
  const parsed = parseAnnexHash(externalUrl);
  if (!parsed) return null;
  if (parsed.childBlockId) {
    return `${parsed.parentSlug}${ANNEX_UUID_SEPARATOR}${parsed.childBlockId}`;
  }
  return parsed.parentSlug.length > 0 ? parsed.parentSlug : null;
}

/** Read `language` from an annex path search string (e.g. `?language=FR`). */
export function annexLanguageFromExternalUrl(
  externalUrl: string,
): string | null {
  const parsed = parseAnnexPath(externalUrl);
  if (!parsed?.search) return null;
  const params = new URLSearchParams(parsed.search.slice(1));
  const language = params.get('language')?.trim();
  return language && language.length > 0 ? language : null;
}
